import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle2, AlertCircle, Eye, Download, X, Loader2, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useUploadDocument,
  useReferralDocuments,
  useGetDocumentDownloadUrl,
  useArchiveDocument,
  type DocumentRow,
} from '@/hooks/useDocuments';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';

interface CheckboxWithFilesProps {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  label: string;
  referralId: string;
  clinicId: string;
  category: string;
  fieldName: string;
  patientId?: string | null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

export function CheckboxWithFiles({
  checked,
  onChange,
  disabled,
  label,
  referralId,
  clinicId,
  category,
  fieldName,
  patientId = null,
}: CheckboxWithFilesProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);

  const uploadDocument = useUploadDocument();
  const { data: documents = [], refetch } = useReferralDocuments(referralId, category, fieldName);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const archiveDocument = useArchiveDocument();

  const hasFiles = documents.length > 0;
  const isUploading = uploadDocument.isPending;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadProgress(0);
      await uploadDocument.mutateAsync({
        clinicId,
        patientId: patientId ?? null,
        referralId,
        referralCategory: category,
        referralFieldName: fieldName,
        file,
        tags: [`Referral - ${category}`, fieldName],
        onProgress: setUploadProgress,
      });
      toast.success(t('documents.upload_success'));
      void refetch();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(t('documents.upload_failed'));
    } finally {
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleView = (doc: DocumentRow) => {
    setPreview({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  };

  const handleDownload = async (doc: DocumentRow) => {
    try {
      const url = await getDownloadUrl.mutateAsync(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('documents.download_failed'));
    }
  };

  const handleRemove = async (doc: DocumentRow) => {
    if (!confirm(t('journal.attachments.remove'))) return;

    try {
      await archiveDocument.mutateAsync(doc.id);
      toast.success(t('journal.attachments.removed'));
      await refetch();
    } catch (error) {
      console.error('Remove failed:', error);
      toast.error(t('journal.attachments.errors.remove_failed'));
    }
  };

  return (
    <div>
      {preview && (
        <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />
      )}

      <div className="flex items-start gap-3">
        <label className="flex items-center gap-3 flex-1 cursor-pointer group">
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-900 dark:text-white flex-1">{label}</span>
        </label>

        {/* Status indicator */}
        {checked && (
          <div className="flex items-center gap-2">
            {hasFiles ? (
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-xs font-medium">{documents.length}</span>
              </div>
            ) : (
              <AlertCircle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            )}

            {/* Upload button */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              disabled={disabled || isUploading}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.pptx,.txt"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30 disabled:opacity-50"
              title={t('documents.upload')}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {uploadProgress > 0 && <span>{uploadProgress}%</span>}
                </>
              ) : (
                  <>
                  <Upload className="h-3 w-3" />
                  {t('documents.upload')}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* File list */}
      {checked && documents.length > 0 && (
        <div className="ml-7 mt-2 space-y-1">
          {documents.map((doc) => {
            const Icon = fileIcon(doc.mime_type);
            return (
              <div
                key={doc.id}
                className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800/60"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleView(doc)}
                    className="block w-full truncate text-left text-xs text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                  >
                    {doc.original_filename}
                  </button>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {formatFileSize(doc.size_bytes)} · {new Date(doc.created_at).toLocaleDateString('da-DK')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleView(doc)}
                  className="rounded p-0.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  title={t('documents.view')}
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDownload(doc)}
                  disabled={getDownloadUrl.isPending}
                  className="rounded p-0.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  title={t('documents.download')}
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleRemove(doc)}
                  className="rounded p-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  title={t('journal.attachments.remove')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
